import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import Loader from "../Components/Loader";
import Message from "../Components/Message";
import BackButton from "../Components/BackButton";
import Product from "../Components/Product";
import {
  useGetFitmentFacetsQuery,
  useGetProductsQuery,
} from "../slices/productsApiSlice";

export default function VehicleSelectScreen() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read from URL
  const queryMake = (searchParams.get("make") || "").trim();
  const queryModel = (searchParams.get("model") || "").trim();
  const queryYear = (searchParams.get("year") || "").trim();
  const queryTrim = (searchParams.get("trim") || "").trim();
  const category = (searchParams.get("category") || "").trim();

  // Controls whether Make is read-only
  const [lockedMake, setLockedMake] = useState(!!queryMake);

  // Controlled state (initialized from URL)
  const [make, setMake] = useState(queryMake);
  const [model, setModel] = useState(queryModel);
  const [year, setYear] = useState(queryYear);
  const [trim, setTrim] = useState(queryTrim);

  // ✅ Sync state whenever URL changes (Back/Forward)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);

    const m = (sp.get("make") || "").trim();
    const mo = (sp.get("model") || "").trim();
    const y = (sp.get("year") || "").trim();
    const t = (sp.get("trim") || "").trim();

    setMake(m);
    setModel(mo);
    setYear(y);
    setTrim(t);

    setLockedMake(!!m);
  }, [location.search]);

  // =========================================================
  // 1) CASCADING FITMENT FACETS
  // =========================================================

  // Makes
  const {
    data: makesData,
    isLoading: isLoadingMakes,
    error: makesError,
  } = useGetFitmentFacetsQuery(
    category ? { category } : {},
    { skip: lockedMake }
  );

  const makes = useMemo(
    () => (makesData?.makes ?? []).map((m) => m.make),
    [makesData]
  );

  // Models
  const {
    data: modelsData,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useGetFitmentFacetsQuery(
    make ? { make, ...(category ? { category } : {}) } : {},
    { skip: !make }
  );

  const models = useMemo(
    () => (modelsData?.models ?? []).map((m) => m.model),
    [modelsData]
  );

  // Years
  const {
    data: yearsData,
    isLoading: isLoadingYears,
    error: yearsError,
  } = useGetFitmentFacetsQuery(
    make && model ? { make, model, ...(category ? { category } : {}) } : {},
    { skip: !(make && model) }
  );

  const years = useMemo(() => yearsData?.years ?? [], [yearsData]);

  // Trims
  const {
    data: trimsData,
    isLoading: isLoadingTrims,
    error: trimsError,
  } = useGetFitmentFacetsQuery(
    make && model && year
      ? { make, model, year, ...(category ? { category } : {}) }
      : {},
    { skip: !(make && model && year) }
  );

  const trims = useMemo(() => trimsData?.trims ?? [], [trimsData]);

  const handleClearMake = () => {
    setLockedMake(false);
    setMake("");
    setModel("");
    setYear("");
    setTrim("");

    // remove make/model/year/trim/pageNumber from URL (keep category)
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("make");
      p.delete("model");
      p.delete("year");
      p.delete("trim");
      p.delete("pageNumber");
      return p;
    });
  };

  // =========================================================
  // 2) PRODUCT RESULTS (merged ResultsScreen logic)
  // =========================================================

  // Build query object from ALL search params (includes pageNumber, category, etc.)
  const query = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  );

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsError,
  } = useGetProductsQuery(query);

  const products = productsData?.products ?? [];
  const pages = productsData?.pages || 1;
  const currentPage = productsData?.page || 1;

  const handleContinue = () => {
    const params = new URLSearchParams();

    if (make) params.set("make", make);
    if (model) params.set("model", model);
    if (year) params.set("year", year);
    if (trim) params.set("trim", trim);
    if (category) params.set("category", category);

    // Reset page when filters change (no pageNumber here → backend treats as page 1)
    setSearchParams(params);
  };

  // Pagination click
  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set("pageNumber", String(page));
    setSearchParams(params);
  };

  return (
    <Container className="py-4">
      <BackButton />
      <h1>Select your vehicle</h1>

      <Card className="mt-3 mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            {/* MAKE */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Make</Form.Label>
              {lockedMake ? (
                <InputGroup>
                  <Form.Control value={make} readOnly className="text-body" />
                  <Button
                    variant="outline-secondary"
                    className="btn-change"
                    onClick={handleClearMake}
                  >
                    Change
                  </Button>
                </InputGroup>
              ) : (
                <>
                  {isLoadingMakes && <Loader small />}
                  {makesError && (
                    <Message variant="danger">
                      {makesError?.data?.message || makesError.error}
                    </Message>
                  )}
                  <Form.Select
                    value={make}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMake(v);
                      setModel("");
                      setYear("");
                      setTrim("");
                    }}
                    disabled={isLoadingMakes}
                    className="vehicle-input"
                  >
                    <option value="">Choose make...</option>
                    {makes.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Form.Select>
                </>
              )}
            </Col>

            {/* MODEL */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Model</Form.Label>
              {isLoadingModels && make ? <Loader small /> : null}
              {modelsError && (
                <Message variant="danger">
                  {modelsError?.data?.message || modelsError.error}
                </Message>
              )}
              <Form.Select
                value={model}
                onChange={(e) => {
                  const v = e.target.value;
                  setModel(v);
                  setYear("");
                  setTrim("");
                }}
                disabled={!make || isLoadingModels}
                className="vehicle-input"
              >
                <option value="">Choose model...</option>
                {models.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* YEAR */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Year</Form.Label>
              {isLoadingYears && model ? <Loader small /> : null}
              {yearsError && (
                <Message variant="danger">
                  {yearsError?.data?.message || yearsError.error}
                </Message>
              )}
              <Form.Select
                value={year}
                onChange={(e) => {
                  const v = e.target.value;
                  setYear(v);
                  setTrim("");
                }}
                disabled={!model || isLoadingYears}
                className="vehicle-input"
              >
                <option value="">Choose year...</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* TRIM (optional) */}
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Trim</Form.Label>
              {isLoadingTrims && year ? <Loader small /> : null}
              {trimsError && (
                <Message variant="danger">
                  {trimsError?.data?.message || trimsError.error}
                </Message>
              )}
              <Form.Select
                value={trim}
                onChange={(e) => setTrim(e.target.value)}
                disabled={!year || isLoadingTrims || trims.length === 0}
                className="vehicle-input"
              >
                <option value="">
                  {trims.length ? "No trim (optional)" : "No trims (optional)"}
                </option>
                {trims.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* CONTINUE */}
            <Col xs={12} className="d-flex justify-content-end mt-3">
              <Button className="vehicle-continue-btn" onClick={handleContinue}>
                Continue
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* RESULTS HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Results</h2>
        {category && (
          <span className="text-muted">
            Category: <strong>{category}</strong>
          </span>
        )}
      </div>

      <div className="text-muted mb-4">
        {(query.make || "")}
        {query.model ? ` • ${query.model}` : ""}
        {query.year ? ` • ${query.year}` : ""}
        {query.category ? ` • ${query.category}` : ""}
      </div>

      {isLoadingProducts && <Loader />}
      {productsError && (
        <Message variant="danger">
          {productsError?.data?.message || productsError.error}
        </Message>
      )}

      <Row>
        {products.map((p) => (
          <Col key={p._id} sm={12} md={6} lg={4} xl={3}>
            <Product product={p} />
          </Col>
        ))}

        {!isLoadingProducts && !productsError && products.length === 0 && (
          <Col>
            <Message>No products found.</Message>
          </Col>
        )}
      </Row>

      {/* PAGINATION */}
      {pages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            {[...Array(pages).keys()].map((x) => {
              const page = x + 1;
              return (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </Pagination.Item>
              );
            })}
          </Pagination>
        </div>
      )}
    </Container>
  );
}
